<section class="mx-auto max-w-[1180px] px-6 py-9 sm:px-8 lg:px-0">
  <div class="mb-6 max-w-3xl">
    <div class="mb-3 flex h-1.5 w-[30px] gap-1">
      <span class="flex-1 rounded bg-(--cmr-vert)"></span>
      <span class="flex-1 rounded bg-(--cmr-rouge)"></span>
      <span class="flex-1 rounded bg-(--cmr-jaune)"></span>
    </div>
    <div class="text-[11px] font-bold uppercase tracking-[0.12em] text-(--text-muted)">{{ 'nav.communities' | translate }}</div>
    <h1 class="m-0 mt-2 text-3xl font-extrabold leading-tight text-(--marine)">{{ 'pages.communities.title' | translate }}</h1>
    <p class="m-0 mt-4 max-w-[620px] text-sm leading-7 text-(--text-muted)">{{ 'pages.communities.subtitle' | translate }}</p>
  </div>

  @if (!isAuthenticated()) {
    <div class="rounded-md border border-(--border-subtle) bg-white px-6 py-12 text-center shadow-(--shadow-card)">
      <p class="m-0 text-sm text-(--text-muted)">{{ 'pages.communities.loginPrompt' | translate }}</p>
      <a routerLink="/auth/login" class="mt-3 inline-block text-sm font-bold text-(--pnud-bleu)">{{ 'nav.login' | translate }}</a>
    </div>
  } @else if (loading()) {
    <p class="py-12 text-center text-[13.5px] text-(--text-muted)" aria-live="polite">{{ 'common.loading' | translate }}</p>
  } @else if (error()) {
    <p class="py-12 text-center text-[13.5px] text-(--state-danger)" role="alert">{{ 'pages.communities.loadError' | translate }}</p>
  } @else {
    <div class="mb-6 grid gap-3.5 sm:grid-cols-3">
      <div class="rounded-md border border-t-[3px] border-(--border-subtle) bg-white p-4 shadow-(--shadow-card)" style="border-top-color: var(--cmr-vert)">
        <div class="text-[26px] font-light text-(--marine) leading-none">{{ spaces().length }}</div>
        <div class="mt-2 text-xs text-(--text-muted)">{{ 'pages.communities.activeSpaces' | translate }}</div>
      </div>
      <div class="rounded-md border border-t-[3px] border-(--border-subtle) bg-white p-4 shadow-(--shadow-card)" style="border-top-color: var(--cmr-rouge)">
        <div class="text-[26px] font-light text-(--marine) leading-none">{{ trending().length }}</div>
        <div class="mt-2 text-xs text-(--text-muted)">{{ 'pages.communities.trendingDiscussions' | translate }}</div>
      </div>
      <div class="rounded-md border border-t-[3px] border-(--border-subtle) bg-white p-4 shadow-(--shadow-card)" style="border-top-color: var(--cmr-jaune)">
        <div class="text-[26px] font-light text-(--marine) leading-none">{{ spaces().length ? spaces()[0].discussionCount : 0 }}</div>
        <div class="mt-2 text-xs text-(--text-muted)">{{ 'pages.communities.discussionsInMostActive' | translate }}</div>
      </div>
    </div>

    <div class="grid gap-8 lg:grid-cols-[1.2fr_320px]">
      <div>
        <div class="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div class="text-sm font-extrabold uppercase tracking-wider text-(--marine)">{{ 'pages.communities.spacesLabel' | translate }} · {{ spaces().length }}</div>
        </div>

        @if (spaces().length > 0) {
          <div class="grid gap-4 sm:grid-cols-[repeat(auto-fill,minmax(258px,1fr))]">
            @for (space of spaces(); track space.id; let i = $index) {
              <a [appScrollReveal]="i" [routerLink]="['/communities', space.slug]"
                class="flex flex-col justify-between gap-2.75 rounded-md border-0 border-t-[3px] bg-white p-5 no-underline shadow-(--shadow-card) transition hover:-translate-y-1 hover:shadow-(--shadow-raised)"
                [style.border-top-color]="styleFor(space.slug).dot">
                <div class="flex items-start justify-between gap-3">
                  <h3 class="m-0 text-base font-extrabold text-(--marine)">{{ space.name }}</h3>
                  <button type="button" (click)="toggleFollow(space, $event)" [disabled]="isFollowPending(space.id)"
                    class="shrink-0 cursor-pointer rounded-full border px-3 py-1 text-[12px] font-bold transition disabled:cursor-not-allowed disabled:opacity-50"
                    [style.background]="isFollowed(space.id) ? styleFor(space.slug).fg : 'white'"
                    [style.border-color]="styleFor(space.slug).fg"
                    [style.color]="isFollowed(space.id) ? 'white' : styleFor(space.slug).fg">
                    {{ (isFollowed(space.id) ? 'pages.communities.following' : 'pages.communities.join') | translate }}
                  </button>
                </div>
                @if (space.description) {
                  <p class="m-0 text-[12.5px] leading-normal text-(--text-muted)">{{ space.description }}</p>
                }
                <div class="mt-auto flex items-center gap-4 text-xs text-(--text-muted)">
                  <span>{{ space.discussionCount }} {{ (space.discussionCount > 1 ? 'pages.communities.discussionsPlural' : 'pages.communities.discussionSingular') | translate }}</span>
                  <span>{{ space.followerCount }} {{ (space.followerCount > 1 ? 'pages.communities.followersPlural' : 'pages.communities.followerSingular') | translate }}</span>
                </div>
              </a>
            }
          </div>
        } @else {
          <div class="rounded-md border border-(--border-subtle) bg-white px-6 py-12 text-center shadow-(--shadow-card)">
            <p class="m-0 text-sm text-(--text-muted)">{{ 'pages.communities.noSpaces' | translate }}</p>
          </div>
        }
      </div>

      <aside class="space-y-5">
        @if (trending().length > 0) {
          <div class="rounded-md border border-(--border-subtle) bg-white p-4.5 shadow-(--shadow-card)">
            <div class="text-[11.5px] font-extrabold uppercase tracking-[0.09em] text-(--text-muted)">{{ 'pages.communities.trendingDiscussionsLabel' | translate }}</div>
            <div class="flex flex-col gap-3">
              @for (d of trending(); track d.id) {
                <div>
                  <div class="text-[13px] font-bold text-(--marine)">{{ d.title }}</div>
                  <div class="text-[11px] text-(--text-muted)">{{ d.replyCount }} {{ (d.replyCount > 1 ? 'pages.communities.repliesPlural' : 'pages.communities.replySingular') | translate }} · {{ d.lastActivityAt | localizedDate }}</div>
                </div>
              }
            </div>
          </div>
        }

        <div class="rounded-md bg-(--pnud-bleu) p-5 text-white">
          <div class="mb-3 text-base font-extrabold">{{ 'pages.communities.askQuestionTitle' | translate }}</div>
          <p class="m-0 text-sm leading-6 text-white/75">{{ 'pages.communities.askQuestionSubtitle' | translate }}</p>
        </div>
      </aside>
    </div>
  }
</section>
